import { Column } from 'primereact/column';
import {
  DataTableExpandedRows,
  DataTableRowMouseEventParams,
} from 'primereact/datatable';
import React, { useCallback, useRef, useState } from 'react';
import Table from '../../../../components/Table/Table';
import {
  EmployeeTimesheet,
  PayrollTimesheetDetail,
  PayrollTimesheetDetails,
} from '../../../../Interfaces/Accounting/ACForms.interfaces';
import { animateCellText } from '@/src/utils/htmlUtils';
import { InputNumber } from 'primereact/inputnumber';
import TimesheetDetailsTable from './TimeSheetDetailsTable';
import JobInput from './fields/JobInput';
import PhaseCodeInput from './fields/PhaseCodeInput';
import ExpandIcon from './ExpandIcon';
import ErrorToast, { showToast } from '../../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';
import {
  addDetailPerDateRange,
  addNewDetail,
  changeTimesheetDate,
  modifyTimesheetValue,
  moveDetailValuesToEmployee,
  moveEmployeeValuesToDetail,
  removeDetail,
  roundHours,
} from './util';
import CalendarIcon from './fields/CalendarIcon';
import { Tooltip } from 'primereact/tooltip';
import RegularHoursInput from './fields/RegularHoursInput';
import { Checkbox } from 'primereact/checkbox';
import { useMutation } from '@tanstack/react-query';
import { postPRUserPreference } from '../../../../services/accounting/PayrollService';

type TimesheetTableProps = {
  employees: EmployeeTimesheet[];
  changeEmployees: (data: EmployeeTimesheet[]) => void;
  periodStart: Date;
  periodEnd: Date;
  companyHours: number;
  onlyPercentageDefault?: boolean;
  showErrors: boolean;
};

const TimesheetTable = ({
  employees,
  periodStart,
  periodEnd,
  changeEmployees,
  companyHours,
  onlyPercentageDefault,
  showErrors,
}: TimesheetTableProps) => {
  const [checkedPercentage, setCheckedPercentage] = useState(
    onlyPercentageDefault
  );
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const toast = useRef<Toast>(null);
  const tooltipRef = useRef<Tooltip>(null);

  const storePreference = useMutation({
    mutationFn: ({ name, value }: { name: string; value: unknown }) => {
      return postPRUserPreference(name, value);
    },
  });

  const onRowHover = (e: DataTableRowMouseEventParams) => {
    const cell = e.originalEvent.target as HTMLElement;
    const row = cell.closest('tr') as HTMLElement;
    animateCellText(row);
  };

  const updateAcc = (
    acc: Record<string, number>,
    hours: PayrollTimesheetDetail
  ) => {
    acc['reg'] += hours.regularHrs ?? 0;
    acc['overtime'] += hours.overTimeHrs ?? 0;
    acc['sick'] += hours.sickHrs ?? 0;
    acc['vacation'] += hours.vacationHrs ?? 0;
    acc['holiday'] += hours.holidayHrs ?? 0;
    acc['perDiem'] += hours.perDiem ?? 0;

    Object.entries(acc).forEach(([key, value]) => {
      acc[key] = roundHours(value);
    });
  };

  const changeTimesheetValue = (
    empID: string,
    key: keyof PayrollTimesheetDetail,
    value?: string | number,
    detailID?: string,
    dateDetailID?: string
  ) => {
    const result = modifyTimesheetValue(
      empID,
      employees,
      key,
      value,
      detailID,
      dateDetailID
    );

    if (result.error) {
      showToast('error', toast, 'Payroll Timesheet', result.error, 3000);
    }

    if (result.data) {
      changeEmployees(result.data);
    }
  };

  const changeDateDetails = (empID: string, detailID: string, date?: Date) => {
    const newData = changeTimesheetDate(employees, empID, detailID, date);

    if (newData) {
      changeEmployees(newData);
    }
  };

  const addDetailRow = (empID: string, detailID?: string) => {
    const newData = addNewDetail(employees, empID, detailID);

    if (newData) {
      changeEmployees(newData);
    }
  };

  const removeDetailRow = (
    empID: string,
    detailID: string,
    dateDetailID?: string
  ) => {
    const newData = removeDetail(employees, empID, detailID, dateDetailID);

    if (newData) {
      changeEmployees(newData);
    }
  };

  const rowExpansionTemplate = (
    data: unknown,
    options: any,
    hiddenCols?: string[]
  ) => {
    const empData = data as EmployeeTimesheet;

    return (
      <TimesheetDetailsTable
        details={empData.details ?? []}
        empID={empData.empID}
        changeDateDetails={changeDateDetails}
        changeTimesheetValue={changeTimesheetValue}
        addDetailRow={addDetailRow}
        removeDetailRow={removeDetailRow}
        maxDate={periodEnd}
        minDate={periodStart}
        hiddenCols={hiddenCols}
        onlyPercentage={checkedPercentage}
        companyHours={companyHours}
        blockPercentage={!empData.salaried}
        showErrors={showErrors}
        showToast={(message) => {
          showToast('info', toast, 'Payroll Timesheet', message, 5000);
        }}
      />
    );
  };

  const footerData = employees.reduce(
    (acc, employee) => {
      acc['total'] += employee.totalHrs ?? 0;
      acc['total'] = roundHours(acc['total']);

      if (employee.useDetails) {
        const details = employee.details ?? [];

        details.forEach((detail) => {
          if (detail.useDateDetails) {
            const dateDetails = detail.dateDetails ?? [];

            dateDetails.forEach((dateDetail) => {
              updateAcc(acc, dateDetail);
            });
          } else {
            updateAcc(acc, detail);
          }
        });
      } else {
        updateAcc(acc, employee);
      }

      return acc;
    },
    {
      total: 0,
      reg: 0,
      overtime: 0,
      sick: 0,
      vacation: 0,
      holiday: 0,
      perDiem: 0,
    }
  );

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />
      <Table
        id="pr-timesheet"
        data={employees}
        dataKey="empID"
        expandedRows={expandedRows}
        customRowExpansionTemplate={rowExpansionTemplate}
        className={`mx-3 dashboardOptionShadow with-expanded noPadding noHover dobleHeader`}
        onRowMouseEnter={useCallback(onRowHover, [])}
        fixedDefaultHeight={790}
        hideColumns={true}
        nonHideableColumns={[
          'REG Hrs',
          'Job Number',
          'Phase Code',
          'Last Name',
          'First Name',
        ]}
        headerContent={
          <div className="flex align-items-center gap-1 text-18px blackText md:ml-4">
            Use only percentage?
            <Checkbox
              onChange={(e) => {
                setCheckedPercentage(e.checked);
                storePreference.mutate({
                  name: 'USE_PERCENTAGE',
                  value: e.checked,
                });
              }}
              className="printHide"
              checked={checkedPercentage}
            />
          </div>
        }
        afterFetching={() => {
          document
            .querySelectorAll(
              '.tt-open-details, .tt-close-details, .certified-warning, .dates-warning'
            )
            .forEach((el) =>
              tooltipRef.current?.updateTargetEvents(el as HTMLElement)
            );
        }}
      >
        <Column
          field="empID"
          header="Emp ID"
          style={{ minWidth: '100px' }}
          headerClassName="tableHeader font-normal text-center"
          className="justify-content-center text-standard blackText tableCell"
          footerClassName="tableFooter"
          sortable
        />
        <Column
          field="lastName"
          header="Last Name"
          body={(pr) => {
            return <div className="scroll-text">{pr.lastName}</div>;
          }}
          style={{ minWidth: '180px', maxWidth: '180px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className=" text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow"
          footerClassName="tableFooter"
          sortable
        />
        <Column
          field="firstName"
          header="First Name"
          body={(pr) => {
            return <div className="scroll-text">{pr.firstName}</div>;
          }}
          style={{ minWidth: '160px', maxWidth: '160px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow"
          footerClassName="tableFooter block text-right border-top-2 border-transparent mt-4 limitBorder relative"
          footer="Total"
          sortable
        />
        <Column
          field="totalHrs"
          header="Total Hrs"
          body={(record) => {
            if (!record.totalHrs) {
              return;
            }

            return record.totalHrs;
          }}
          style={{ minWidth: '100px', maxWidth: '100px' }}
          headerClassName="tableHeader font-normal"
          className="text-standard blackText tableCell justify-content-center"
          footerClassName="tableFooter block text-center border-top-2 mt-4"
          footer={footerData['total']}
          sortable
        />
        <Column
          header="Dates"
          body={(record) => {
            if (expandedRows[record.empID]) {
              return;
            }

            const onClick = () => {
              tooltipRef.current?.hide();
              setExpandedRows((rows) => {
                rows[record.empID] = true;

                return { ...rows };
              });
            };

            if (
              record.details?.some(
                (detail: PayrollTimesheetDetails) =>
                  detail.certified && (!detail.date || !detail.totalHrs)
              )
            ) {
              return (
                <i
                  className="pi pi-exclamation-triangle text-17px errorText mx-auto certified-warning cursor-pointer"
                  data-pr-position="right"
                  data-pr-tooltip="Certified Job selected. Please fill out the hours by day!"
                  onClick={onClick}
                />
              );
            }

            const dateNotFromPeriod = record.details?.some(
              (detail: PayrollTimesheetDetails) => {
                if (!detail.date) {
                  return false;
                }

                const date = new Date(detail.date);

                return (
                  date.getTime() < periodStart.getTime() ||
                  periodEnd.getTime() < date.getTime()
                );
              }
            );
            if (dateNotFromPeriod) {
              return (
                <i
                  className="pi pi-exclamation-triangle text-17px errorText mx-auto dates-warning cursor-pointer"
                  data-pr-position="right"
                  data-pr-tooltip="Update the dates! Some are not part of the current period"
                  onClick={onClick}
                />
              );
            }

            if (record.useDetails) {
              return (
                <div
                  className="mx-auto printHide text-center totalColor hover:underline cursor-pointer"
                  onClick={onClick}
                >
                  Expand for details
                </div>
              );
            }

            return;
          }}
          style={{ minWidth: '120px', maxWidth: '120px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter block text-right border-top-2 mt-4"
        />
        <Column
          field="regularHrs"
          header="REG Hrs"
          body={(record) => {
            return (
              <span className="text-center w-full">
                <RegularHoursInput
                  id={`reg_${record.empID}`}
                  regularHrs={record.regularHrs}
                  onlyPercentage={checkedPercentage}
                  blockPercentage={!record.salaried}
                  onChange={(value) => {
                    changeTimesheetValue(
                      record.empID,
                      'regularHrs',
                      value ?? undefined
                    );
                  }}
                  companyHours={companyHours}
                  disabled={record.useDetails}
                />
              </span>
            );
          }}
          style={{ minWidth: '95px', maxWidth: '95px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter block text-center border-top-2 mt-4"
          footer={footerData['reg']}
        />
        <Column
          field="overTimeHrs"
          header="OT Hrs"
          body={(record) => {
            return (
              <span className="text-center w-full">
                <InputNumber
                  className="printHide"
                  inputClassName="w-full blackText text-standard text-center"
                  value={record.overTimeHrs}
                  onBlur={(event) => {
                    changeTimesheetValue(
                      record.empID,
                      'overTimeHrs',
                      parseFloat(event.target.value?.replace(/,/g, '')) ??
                        undefined
                    );
                  }}
                  disabled={record.useDetails}
                  maxFractionDigits={2}
                />
                <span className="printMedia printShow">
                  {record.overTimeHrs}
                </span>
              </span>
            );
          }}
          style={{ minWidth: '95px', maxWidth: '95px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter block text-center border-top-2 mt-4"
          footer={footerData['overtime']}
        />
        <Column
          field="sickHrs"
          header="Sick Hrs"
          body={(record) => {
            return (
              <span className="text-center w-full">
                <InputNumber
                  className="printHide"
                  inputClassName="w-full blackText text-standard text-center"
                  value={record.sickHrs}
                  onBlur={(event) => {
                    changeTimesheetValue(
                      record.empID,
                      'sickHrs',
                      parseFloat(event.target.value?.replace(/,/g, '')) ??
                        undefined
                    );
                  }}
                  disabled={record.useDetails}
                  maxFractionDigits={2}
                />
                <span className="printMedia printShow">{record.sickHrs}</span>
              </span>
            );
          }}
          style={{ minWidth: '95px', maxWidth: '95px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter block text-center border-top-2 mt-4"
          footer={footerData['sick']}
        />
        <Column
          field="vacationHrs"
          header="Vacation Hrs"
          body={(record) => {
            return (
              <span className="text-center w-full">
                <InputNumber
                  className="printHide"
                  inputClassName="w-full blackText text-standard text-center"
                  value={record.vacationHrs}
                  onBlur={(event) => {
                    changeTimesheetValue(
                      record.empID,
                      'vacationHrs',
                      parseFloat(event.target.value?.replace(/,/g, '')) ??
                        undefined
                    );
                  }}
                  disabled={record.useDetails}
                  maxFractionDigits={2}
                />
                <span className="printMedia printShow">
                  {record.vacationHrs}
                </span>
              </span>
            );
          }}
          style={{ minWidth: '105px', maxWidth: '105px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter block text-center border-top-2 mt-4"
          footer={footerData['vacation']}
        />
        <Column
          field="holidayHrs"
          header="Holiday Hrs"
          body={(record) => {
            return (
              <span className="text-center w-full">
                <InputNumber
                  className="printHide"
                  inputClassName="w-full blackText text-standard text-center"
                  value={record.holidayHrs}
                  onBlur={(event) => {
                    changeTimesheetValue(
                      record.empID,
                      'holidayHrs',
                      parseFloat(event.target.value?.replace(/,/g, '')) ??
                        undefined
                    );
                  }}
                  disabled={record.useDetails}
                  maxFractionDigits={2}
                />
                <span className="printMedia printShow">
                  {record.holidayHrs}
                </span>
              </span>
            );
          }}
          style={{ minWidth: '95px', maxWidth: '95px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter block text-center border-top-2 mt-4"
          footer={footerData['holiday']}
        />
        <Column
          field="jobNumber"
          header="Job Number"
          body={(record) => {
            if (expandedRows[record.empID]) {
              return;
            }

            if (record.useDetails) {
              return null;
            }

            return (
              <JobInput
                value={record.job}
                disableFetch={true}
                onChange={(event, certified) => {
                  if (certified) {
                    moveEmployeeValuesToDetail(record);
                    record.details[0].certified = true;
                    changeTimesheetValue(
                      record.empID,
                      'job',
                      event.value,
                      record.details[0].id
                    );
                    setExpandedRows((rows) => {
                      rows[record.empID] = true;

                      return { ...rows };
                    });

                    showToast(
                      'info',
                      toast,
                      'Payroll Timesheet',
                      'The selected Job is certified, this requires the hours to be entered by day!',
                      5000
                    );
                  } else {
                    changeTimesheetValue(record.empID, 'job', event.value);
                  }

                  if (record.phaseCode) {
                    changeTimesheetValue(record.empID, 'phaseCode', undefined);
                  }
                }}
              />
            );
          }}
          style={{ minWidth: '300px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter"
        />
        <Column
          field="phaseCode"
          header="Phase Code"
          body={(record) => {
            if (
              !record.job ||
              record.job === 'N/A' ||
              expandedRows[record.empID]
            ) {
              return;
            }

            if (record.useDetails) {
              return null;
            }

            return (
              <PhaseCodeInput
                value={record.phaseCode}
                onChange={(event) => {
                  changeTimesheetValue(record.empID, 'phaseCode', event.value);
                }}
                job={record.job}
              />
            );
          }}
          style={{ minWidth: '300px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter"
        />
        <Column
          field="perDiem"
          header="Per Diem (Days)"
          body={(record) => {
            return (
              <span className="text-center w-full">
                <InputNumber
                  className="printHide"
                  inputClassName="w-full blackText text-standard text-center"
                  min={0}
                  value={record.perDiem}
                  onBlur={(event) => {
                    changeTimesheetValue(
                      record.empID,
                      'perDiem',
                      parseFloat(event.target.value?.replace(/,/g, '')) ??
                        undefined
                    );
                  }}
                  disabled={record.useDetails}
                />
                <span className="printMedia printShow">{record.perDiem}</span>
              </span>
            );
          }}
          style={{ minWidth: '100px', maxWidth: '100px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter block text-center border-top-2 mt-4"
          footer={footerData['perDiem']}
        />
        <Column
          header={() => {
            return (
              <i
                className="pi pi-plus text-standard cursor-pointer tt-open-details"
                onClick={() => {
                  const expandedCopy: DataTableExpandedRows = {};
                  employees.forEach((emp) => {
                    if (emp.useDetails) {
                      expandedCopy[emp.empID] = true;
                    }
                  });
                  setExpandedRows(expandedCopy);
                }}
                data-pr-position="left"
                data-pr-tooltip="Expand all details"
              />
            );
          }}
          body={(record) => {
            return (
              <ExpandIcon
                expanded={expandedRows[record.empID]}
                onOpen={() => {
                  setExpandedRows((rows) => {
                    rows[record.empID] = true;
                    moveEmployeeValuesToDetail(record);

                    return { ...rows };
                  });
                }}
                onClose={() => {
                  setExpandedRows((rows) => {
                    delete rows[record.empID];
                    moveDetailValuesToEmployee(record);

                    return { ...rows };
                  });
                }}
              />
            );
          }}
          style={{ maxWidth: '30px' }}
          headerClassName={`tableHeader font-normal`}
          className={`text-standard blackText printHide tableCell justify-content-center`}
          footerClassName="tableFooter"
        />
        <Column
          header={() => {
            return (
              <i
                className="pi pi-minus text-standard cursor-pointer tt-close-details"
                onClick={() => {
                  setExpandedRows({});
                }}
                data-pr-position="left"
                data-pr-tooltip="Collapse all details"
              />
            );
          }}
          body={(record) => {
            return (
              <CalendarIcon
                showIcon={expandedRows[record.empID]}
                maxDate={periodEnd}
                minDate={periodStart}
                onChange={(e) => {
                  const datesList = e.value as Date[];

                  if (datesList.length === 2 && datesList[1] !== null) {
                    const newData = addDetailPerDateRange(
                      employees,
                      record.empID,
                      datesList
                    );

                    if (newData) {
                      changeEmployees(newData);
                    }
                  }
                }}
              />
            );
          }}
          style={{ minWidth: '30px', maxWidth: '30px' }}
          headerClassName={`tableHeader font-normal`}
          className={`text-standard blackText printHide tableCell justify-content-center overflow-visible`}
          footerClassName="tableFooter"
        />
      </Table>
      <Tooltip
        ref={tooltipRef}
        target=".tt-open-details, .tt-close-details, .certified-warning, .dates-warning"
      />
    </React.Fragment>
  );
};

export default TimesheetTable;
